
import { tool } from '@openai/agents-realtime';
import { z } from 'zod';
import { UnifiedSearchService } from '@/lib/services/unified-search.service';
import { supabase } from '@/lib/supabase/client';

function emitBrowserEvent(name: string, detail: unknown) {
  const target = (globalThis as { window?: Pick<EventTarget, 'dispatchEvent'> }).window;
  if (!target) return;
  try {
    target.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {}
}

function emitProgress(step: 'vector' | 'parametric' | 'web', status: 'started' | 'done' | 'skipped', info?: Record<string, unknown>) {
  emitBrowserEvent('remy:progress', { step, status, ...(info || {}) });
}

export interface RichVenue {
  id?: string;
  place_name: string;
  google_place_id?: string;

  city: string;
  detected_neighborhood?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;

  vibe?: string;              // "Intimate, romantic"
  best_for?: string;          // "Date nights, locals"
  insider_tip?: string;       // "Book ahead for weekends"
  highlights?: string;        // "Handmade pasta, risotto"
  description?: string;       // Full description

  category?: string;
  cuisine_type?: string;      // "Italian, Northern Italian"

  rating?: number;            // 98% coverage
  price_level?: number;       // 93% coverage (1-4 scale)
  review_count?: number;

  photo_url?: string;         // 84% coverage

  business_status?: string;
  opening_hours?: any[];      // 72% coverage
  phone?: string;             // 66% coverage
  website?: string;           // 69% coverage

  recommender_name?: string;
  additional_recommenders?: any[];

  source: 'vector' | 'parametric' | 'web' | 'merged';
  similarity_score?: number;
  validation_status?: 'validated' | 'failed' | 'pending';
  final_score?: number;
}

export const searchAllTool = tool({
  name: 'search_all',
  description: 'Searches curated venues. ALWAYS call this for any venue search. CRITICAL: Extract neighborhood, vibe, cuisine from the query and pass in context object.',
  parameters: z.object({
    query: z.string().describe('The full natural language query from the user'),
    city: z.string().describe('City to search in (e.g., "New York", "Miami")'),
    context: z.object({
      neighborhood: z.string().nullable().optional().describe('REQUIRED if user mentions a neighborhood. Extract from query (e.g., "South Beach", "West Village", "Wynwood")'),
      price_range: z.string().nullable().optional().describe('Extract if mentioned: "cheap", "expensive", "moderate"'),
      vibe: z.string().nullable().optional().describe('Extract if mentioned: "romantic", "casual", "lively", "quiet"'),
      cuisine: z.string().nullable().optional().describe('Extract if mentioned: "Italian", "sushi", "Mexican"'),
      limit: z.number().default(10).describe('Max results to return')
    }).nullable().optional().describe('CRITICAL: Extract structured filters from the query. If user says "South Beach", you MUST set context.neighborhood = "South Beach"'),
    features: z.object({
      enhanced_ranking: z.boolean().default(false)
    }).nullable().optional()
  }),

  execute: async ({ query, city, context, features }) => {
    console.log('[SearchAll] Starting curated search:', { query, city, context });

    try {
      const vectorT0 = Date.now();
      emitProgress('vector', 'started');
      const vectorResults = await searchVectors(query, city, context);
      emitProgress('vector', 'done', { count: vectorResults.length, ms: Date.now() - vectorT0 });
      emitProgress('parametric', 'skipped');
      emitProgress('web', 'skipped');
      const allVenues = vectorResults;
      const uniqueVenues = deduplicateVenues(allVenues);

      console.log('[SearchAll] After dedup:', uniqueVenues.length);

      const enrichedVenues = await enrichVenues(uniqueVenues, city);

      console.log('[SearchAll] After enrichment:', enrichedVenues.length);

      const useEnhanced = Boolean(features?.enhanced_ranking) ||
        ((import.meta as any).env?.VITE_ENHANCED_RANKING === 'true') ||
        (typeof process !== 'undefined' && (process as any).env?.ENHANCED_RANKING === 'true');
      const rankedVenues = (useEnhanced ? rankVenuesEnhanced : rankVenues)(enrichedVenues, { query, city, ...context });

      const top5ToDiscuss = rankedVenues.slice(0, 5).map((venue, index) => ({
        venue_id: venue.id || venue.google_place_id || venue.place_name,
        place_name: venue.place_name,
        order: index + 1,
        why_selected: venue.description || `Top ${index + 1} match for "${query}"`,
        neighborhood: venue.detected_neighborhood,
        vibe: venue.vibe,
        cuisine: venue.cuisine_type,
        price_level: venue.price_level
      }));

      emitBrowserEvent('remy:venuesPartial', {
        venues: rankedVenues,
        reason: 'final_results',
        top5_to_discuss: top5ToDiscuss
      });

      console.log('[SearchAll] Returning', rankedVenues.length, 'total venues');
      console.log('[SearchAll] 🎯 Top 5 to discuss:', top5ToDiscuss.map(v => `${v.order}. ${v.place_name}`));

      return {
        venues: rankedVenues,  // All venues for scrolling
        top5_to_discuss: top5ToDiscuss,  // Authoritative list for narration + highlights
        breakdown: {
          vector_count: vectorResults.length,
          total_found: rankedVenues.length,
          validated_count: enrichedVenues.filter(v => v.validation_status === 'validated').length
        },
        patterns: extractPatterns(rankedVenues),
        instructions_for_voice: 'Discuss the top5_to_discuss venues in order. As you mention each venue name, the UI will automatically highlight its pin on the map and show the venue card.'
      };
    } catch (error: any) {
      console.error('[SearchAll] Error:', error);
      const fallback = await searchVectors(query, city, context);
      return {
        venues: fallback.slice(0, 5),
        breakdown: { vector_count: fallback.length },
        error: error.message
      };
    }
  }
});

async function searchVectors(query: string, city: string, context?: any): Promise<RichVenue[]> {
  let allResults: any[] = [];

  const filters: any = {};
  if (context?.neighborhood) {
    filters.neighborhoods = [context.neighborhood];
    console.log(`[SearchVectors] Applying neighborhood filter from context: ${context.neighborhood}`);
  } else {
    const neighborhoodPatterns = [
      /\b(south beach|miami beach|mid-beach|north beach)\b/i,
      /\b(wynwood|edgewater|design district|brickell|coconut grove|coral gables|little havana|downtown miami)\b/i,
      /\b(west village|east village|soho|tribeca|chelsea|williamsburg|lower east side|les|greenwich village)\b/i,
      /\b(upper east side|upper west side|midtown|financial district|harlem)\b/i
    ];

    for (const pattern of neighborhoodPatterns) {
      const match = query.match(pattern);
      if (match) {
        const hood = match[1];
        filters.neighborhoods = [hood];
        console.warn(`[SearchVectors] ⚠️ GPT-5 didn't pass context.neighborhood - extracted "${hood}" from query as fallback`);
        break;
      }
    }

    if (!filters.neighborhoods) {
      console.warn('[SearchVectors] ⚠️ No neighborhood in context and could not extract from query');
    }
  }

  try {
    const resp = await fetch('/.netlify/functions/vector-search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query,
        city,
        limit: 30,
        filters: Object.keys(filters).length > 0 ? filters : undefined
      })
    });
    if (resp.ok) {
      const data: unknown = await resp.json();
      allResults = data && typeof data === 'object' && 'venues' in data && Array.isArray(data.venues)
        ? data.venues : [];
    }
  } catch {}
  // Fallback to legacy service if backend is unavailable
  if (allResults.length === 0) {
    const service = new UnifiedSearchService();
    await service.initialize();
    const results = await service.search(query, city);
    allResults = [...results.curated, ...results.discovered];
  }

  return allResults.map(v => ({
    id: v.id,
    place_name: v.place_name || v.name,
    google_place_id: (v as any).google_place_id || (v as any).place_id,

    city: city,
    detected_neighborhood: v.detected_neighborhood || v.neighborhood,
    formatted_address: (v as any).formatted_address || (v as any).address,
    latitude: v.latitude,
    longitude: v.longitude,

    vibe: (v as any).vibe,
    best_for: (v as any).best_for,
    insider_tip: (v as any).insider_tip,
    highlights: (v as any).highlights,
    description: (v as any).description,

    category: v.category,
    cuisine_type: v.cuisine_type,

    rating: v.rating,
    price_level: v.price_level,
    review_count: (v as any).review_count || (v as any).user_ratings_total,

    photo_url: (v as any).photo || (Array.isArray((v as any).photos) ? (v as any).photos[0] : undefined),

    business_status: v.business_status || 'OPERATIONAL',
    opening_hours: (v as any).opening_hours,
    phone: (v as any).phone,
    website: (v as any).website,

    recommender_name: (v as any).recommender_name,
    additional_recommenders: (v as any).additional_recommenders,

    source: 'vector' as const,
    similarity_score: typeof (v as any).similarity === 'number' ? (v as any).similarity : undefined,
    validation_status: 'validated', // Our DB is trusted
    final_score: 0
  }));
}


function deduplicateVenues(venues: RichVenue[]): RichVenue[] {
  const seen = new Map<string, RichVenue>();

  for (const venue of venues) {
    const key = createVenueKey(venue);

    if (seen.has(key)) {
      const existing = seen.get(key)!;
      seen.set(key, mergeVenueData(existing, venue));
    } else {
      seen.set(key, venue);
    }
  }

  return Array.from(seen.values());
}

function createVenueKey(venue: RichVenue): string {
  if (typeof venue.latitude === 'number' && typeof venue.longitude === 'number') {
    const latKey = Math.round(venue.latitude * 1000);
    const lngKey = Math.round(venue.longitude * 1000);
    return `${latKey}_${lngKey}`;
  }

  const name = venue.place_name.toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .substring(0, 10);

  const location = venue.detected_neighborhood?.toLowerCase() ||
                   venue.city.toLowerCase();

  return `${name}_${location}`;
}

function mergeVenueData(existing: RichVenue, newVenue: RichVenue): RichVenue {
  return {
    ...existing,
    vibe: existing.vibe || newVenue.vibe,
    best_for: existing.best_for || newVenue.best_for,
    insider_tip: existing.insider_tip || newVenue.insider_tip,
    description: existing.description || newVenue.description,

    source: 'merged',
    similarity_score: Math.max(
      existing.similarity_score || 0,
      newVenue.similarity_score || 0
    )
  };
}

async function enrichVenues(venues: RichVenue[], city: string): Promise<RichVenue[]> {
  const { data, error } = await supabase.auth.getSession();
  const accessToken = data?.session?.access_token;
  if (error || !accessToken) {
    return venues;
  }

  // Only venues that reach the DB-match step below need recommendations
  // rows: already-failed venues return early, and fully populated vector
  // venues never call findDbMatch. When nothing needs a match, skip the
  // read entirely.
  const matchCandidates = venues.filter((venue) => {
    if (venue.validation_status === 'failed') return false;
    if (venue.source === 'vector' && venue.photo_url && venue.website && venue.phone) return false;
    return venue.source === 'parametric' || venue.source === 'web' || venue.source === 'vector';
  });

  // Two-step read replacing the unfiltered 22-column city read: a narrow
  // id/place_name scan of the city, the same exact-then-unique-partial
  // match in memory, then the wide columns only for the ids that matched.
  // The wide read tracks the ~10-30 candidates instead of the whole city
  // catalog (previously up to the 1000-row PostgREST cap) and is skipped
  // when no candidate matches.
  const matchByName = new Map<string, Record<string, unknown> & { place_name?: string }>();
  if (matchCandidates.length > 0) {
    const { data: nameRows } = await supabase
      .from('recommendations')
      .select('id, place_name')
      .eq('city', city);
    const cityNames = (nameRows || []) as Array<{ id: string; place_name?: string }>;
    const exactByName = new Map<string, { id: string }>();
    for (const row of cityNames) {
      const key = (row.place_name || '').toLowerCase();
      if (key && !exactByName.has(key)) exactByName.set(key, row);
    }

    // A unique match only: "Blue Cafe" must not validate against "The Blue
    // Cafe" just because it sorted first. Exact normalized equality wins;
    // otherwise require exactly one partial match — ambiguity falls through
    // to the enrichment path instead of marking the wrong venue validated.
    const matchIdFor = (placeName: string): string | undefined => {
      const needle = (placeName || '').toLowerCase();
      if (!needle) return undefined;
      const exact = exactByName.get(needle);
      if (exact) return exact.id;
      const partials = cityNames.filter((r) => (r.place_name || '').toLowerCase().includes(needle));
      return partials.length === 1 ? partials[0].id : undefined;
    };

    const matchIdByName = new Map<string, string>();
    for (const venue of matchCandidates) {
      const key = (venue.place_name || '').toLowerCase();
      if (!key || matchIdByName.has(key)) continue;
      const id = matchIdFor(key);
      if (id !== undefined) matchIdByName.set(key, id);
    }

    if (matchIdByName.size > 0) {
      const { data: matchedRows } = await supabase
        .from('recommendations')
        .select('id, place_name, google_place_id, detected_neighborhood, formatted_address, latitude, longitude, vibe, best_for, insider_tip, highlights, description, category, cuisine_type, rating, price_level, photo_url, business_status, opening_hours, phone, website, recommender_name, additional_recommenders')
        .in('id', [...matchIdByName.values()]);
      const rowById = new Map(
        ((matchedRows || []) as Array<Record<string, unknown> & { id: string }>).map((r) => [r.id, r])
      );
      for (const [name, id] of matchIdByName) {
        const row = rowById.get(id);
        if (row) matchByName.set(name, row);
      }
    }
  }

  const findDbMatch = (placeName: string) => matchByName.get((placeName || '').toLowerCase());

  const enrichmentTasks = venues.map(async (venue) => {
    if (venue.source === 'vector') {
      const needsSupplement = !venue.photo_url || !venue.website || !venue.phone;
      if (!needsSupplement) return venue;
    }

    if (venue.validation_status === 'failed') {
      return venue;
    }

    try {
      if (venue.source === 'parametric' || venue.source === 'web' || venue.source === 'vector') {
        const dbMatch = findDbMatch(venue.place_name);

        if (dbMatch && (venue.source === 'parametric' || venue.source === 'web')) {
          return {
            ...venue,
            ...mapSupabaseToRichVenue(dbMatch),
            source: 'merged',
            validation_status: 'validated'
          };
        }

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000); // Increased from 2500ms to 5000ms
        try {
          const resp = await fetch('/.netlify/functions/enrich-venue', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            signal: controller.signal,
            body: JSON.stringify({
              place_name: venue.place_name,
              city,
              address: venue.formatted_address
            })
          });
          clearTimeout(timeout);
          if (resp.ok) {
            const payload = await resp.json() as {
              data?: {
                location?: { lat?: unknown; lng?: unknown };
                business_status?: unknown;
                google_place_id?: unknown;
                photo_url?: unknown;
                website?: unknown;
                phone?: unknown;
                international_phone?: unknown;
              };
            };
            const data = payload.data || {};
            const lat = data?.location?.lat;
            const lng = data?.location?.lng;
            const status = typeof data.business_status === 'string' ? data.business_status : undefined;
            const googlePlaceId = typeof data.google_place_id === 'string'
              ? data.google_place_id
              : undefined;
            const photoUrl = typeof data.photo_url === 'string' ? data.photo_url : undefined;
            const website = typeof data.website === 'string' ? data.website : undefined;
            const phone = typeof data.phone === 'string' ? data.phone : undefined;
            const internationalPhone = typeof data.international_phone === 'string'
              ? data.international_phone
              : undefined;
            if (status === 'CLOSED_PERMANENTLY' || status === 'CLOSED_TEMPORARILY') {
              console.log(`[Enrich] Filtering out closed venue: ${venue.place_name} (status: ${status})`);
              return {
                ...venue,
                validation_status: 'failed'
              };
            }

            const enriched: RichVenue = {
              ...venue,
              latitude: typeof lat === 'number' ? lat : venue.latitude,
              longitude: typeof lng === 'number' ? lng : venue.longitude,
              business_status: status || venue.business_status,
              google_place_id: googlePlaceId || venue.google_place_id,
              // Show venue as pending if we have coords, even without full enrichment
              validation_status: status === 'OPERATIONAL' ? 'validated' :
                                 (lat && lng) ? 'pending' : 'failed',
              photo_url: venue.photo_url || photoUrl,
              website: website || venue.website,
              phone: phone || internationalPhone || venue.phone,
              source: venue.source // preserve literal type
            };
            return enriched;
          }
        } catch {
          // ignore; fall through to pending
        } finally {
          clearTimeout(timeout);
        }

        return {
          ...venue,
          validation_status: 'pending'
        };
      }

      return venue;
    } catch (error) {
      console.warn(`[Enrich] Failed for ${venue.place_name}:`, error);
      return {
        ...venue,
        validation_status: 'failed'
      };
    }
  });

  const enriched = (await Promise.all(enrichmentTasks)) as RichVenue[];

  // Filter out failed validations unless they're highly relevant
  return enriched.filter((v: RichVenue) =>
    v.validation_status !== 'failed' ||
    v.similarity_score! > 0.8
  );
}

function mapSupabaseToRichVenue(record: any): Partial<RichVenue> {
  return {
    id: record.id,
    google_place_id: record.google_place_id,

    detected_neighborhood: record.detected_neighborhood,
    formatted_address: record.formatted_address,
    latitude: record.latitude,
    longitude: record.longitude,

    vibe: record.vibe,
    best_for: record.best_for,
    insider_tip: record.insider_tip,
    highlights: record.highlights,
    description: record.description,

    category: record.category,
    cuisine_type: record.cuisine_type,

    rating: record.rating,
    price_level: record.price_level,

    photo_url: record.photo_url,

    business_status: record.business_status,
    opening_hours: record.opening_hours,
    phone: record.phone,
    website: record.website,

    recommender_name: record.recommender_name,
    additional_recommenders: record.additional_recommenders
  };
}

function rankVenues(venues: RichVenue[], context: any): RichVenue[] {
  return venues
    .map(venue => {
      let score = 0;

      if (venue.source === 'vector') {
        score += 40 + (venue.similarity_score || 0) * 60; // 40-100
      } else if (venue.source === 'parametric') {
        score += 30; // Base score for GPT suggestions
      } else if (venue.source === 'merged') {
        score += 50; // Bonus for appearing in multiple sources
      }

      if (venue.validation_status === 'validated') {
        score += 20;
      }

      if (venue.vibe && venue.insider_tip) score += 15;
      if (venue.photo_url) score += 10;
      if (venue.rating && venue.rating > 4.0) score += 10;

      if (context.neighborhood && venue.detected_neighborhood === context.neighborhood) {
        score += 20;
      }
      if (context.price_range) {
        const priceMatch = matchPriceRange(venue.price_level, context.price_range);
        if (priceMatch) score += 15;
      }

      venue.final_score = score;
      return venue;
    })
    .sort((a, b) => b.final_score! - a.final_score!);
}

function rankVenuesEnhanced(venues: RichVenue[], context: any): RichVenue[] {
  const q = String(context?.query || '').toLowerCase();
  const cuisineSet = new Set<string>();
  if (/french/.test(q)) cuisineSet.add('french');
  if (/italian/.test(q)) cuisineSet.add('italian');

  const isDateNight = /date(\s|-)night|romantic|cozy/.test(q) || /date/i.test(String(context?.vibe || ''));
  const lowerManhattanHoods = new Set(['soho','tribeca','west village','greenwich village','nolita','lower east side','les','noho','chinatown','financial district','battery park']);

  return venues
    .map(venue => {
      let score = 0;

      if (venue.source === 'vector') score += 30 + (venue.similarity_score || 0) * 50;
      if (venue.source === 'parametric') score += 25;
      if (venue.source === 'merged') score += 45;

      if (venue.validation_status === 'validated') score += 15;
      if (venue.photo_url) score += 8;
      if (venue.rating && venue.rating > 4.2) score += 8;

      const venueCuisine = (venue.cuisine_type || '').toLowerCase();
      for (const c of cuisineSet) if (venueCuisine.includes(c)) score += 20;

      const hood = (venue.detected_neighborhood || '').toLowerCase();
      if (hood && lowerManhattanHoods.has(hood)) score += 20;

      if (isDateNight) {
        const vibe = (venue.vibe || '').toLowerCase();
        if (/(romantic|intimate|cozy|date)/.test(vibe)) score += 15;
      }

      venue.final_score = score;
      return venue;
    })
    .sort((a, b) => b.final_score! - a.final_score!);
}

function matchPriceRange(level: number | undefined, range: string): boolean {
  if (!level || !range) return false;

  const rangeMap: Record<string, number[]> = {
    'cheap': [1],
    'moderate': [2],
    'expensive': [3],
    'luxury': [4],
    'under $20': [1],
    'under $50': [1, 2],
    'over $50': [3, 4]
  };

  const acceptable = rangeMap[range.toLowerCase()];
  return acceptable ? acceptable.includes(level) : false;
}

function extractPatterns(venues: RichVenue[]) {
  const vibes = venues
    .map(v => v.vibe)
    .filter(Boolean)
    .slice(0, 5);

  const cuisines = venues
    .map(v => v.cuisine_type)
    .filter(Boolean)
    .slice(0, 5);

  const neighborhoods = venues
    .map(v => v.detected_neighborhood)
    .filter(Boolean)
    .slice(0, 5);

  return {
    dominant_vibes: [...new Set(vibes)],
    cuisine_types: [...new Set(cuisines)],
    neighborhoods: [...new Set(neighborhoods)],
    avg_price: venues.reduce((acc, v) => acc + (v.price_level || 2), 0) / venues.length,
    has_rich_data_pct: venues.filter(v => v.vibe && v.insider_tip).length / venues.length
  };
}
